import React, { Fragment, useEffect, useState } from "react";
import { Form, FormGroup, Input, Label, Spinner } from "reactstrap";
import { Btn, H4, P } from "../AbstractElements";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff, Lock, Shield } from "react-feather";
import credoraToast from "../Components/Credora/Common/credoraToast";
import { acceptPasswordReset, fetchPasswordResetPreview } from "../api/auth";
import { brandLogo } from "../assets/brand/credoraLogos";
import authIllustration from "../assets/images/login/auth-img.svg";

const FEATURES = [
  "Secure one-time reset link from your administrator",
  "Choose a strong password of at least 8 characters",
  "Sign in immediately after updating your password",
];

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const navigate = useNavigate();
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [togglePassword, setTogglePassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) {
      setError("Missing reset link. Ask your administrator to send a new password reset email.");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    fetchPasswordResetPreview(token)
      .then((data) => setPreview(data))
      .catch((err) => {
        setError(err.response?.data?.detail || "This reset link is invalid or has expired.");
      })
      .finally(() => setLoading(false));
  }, [token]);

  const passwordsMatch = password === passwordConfirm;
  const canSubmit =
    token &&
    preview &&
    password.length >= 8 &&
    passwordConfirm.length >= 8 &&
    passwordsMatch &&
    !submitting;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) {
      return;
    }
    setSubmitting(true);
    try {
      await acceptPasswordReset({ token, password, password_confirm: passwordConfirm });
      credoraToast.success("Password updated. You can sign in now.");
      navigate(`${process.env.PUBLIC_URL}/login`);
    } catch (err) {
      credoraToast.error(err.response?.data?.detail, "Could not reset password.");
    } finally {
      setSubmitting(false);
    }
  };

  const loginPath = `${process.env.PUBLIC_URL}/login`;

  return (
    <Fragment>
      <div className="credora-login">
        <div className="credora-login__shell">
          <aside className="credora-login__brand" aria-hidden="true">
            <div className="credora-login__brand-inner">
              <p className="credora-login__eyebrow">Account security</p>
              <h1 className="credora-login__headline">
                Reset your password
                <span> and get back to Credora360</span>
              </h1>
              <P attrPara={{ className: "credora-login__tagline" }}>
                Use the secure link from your email to choose a new password for your staff account.
              </P>
              <ul className="credora-login__features">
                {FEATURES.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
              <div className="credora-login__illustration">
                <img src={authIllustration} alt="" />
              </div>
            </div>
          </aside>

          <main className="credora-login__panel">
            <div className="credora-login__card">
              <Link className="credora-login__logo credora-brand-logo" to="/">
                <img src={brandLogo} alt="Credora360" className="img-fluid credora-brand-logo__img" />
              </Link>

              <div className="credora-login__form-header">
                <H4>Reset your password</H4>
                <P>Choose a new password to continue signing in.</P>
              </div>

              {loading ? (
                <div className="text-center py-4">
                  <Spinner color="primary" className="mb-3" />
                  <P attrPara={{ className: "text-muted mb-0" }}>Verifying reset link…</P>
                </div>
              ) : error ? (
                <div className="credora-login__form">
                  <P attrPara={{ className: "text-danger text-center mb-4" }}>{error}</P>
                  <Link to={loginPath} className="btn btn-primary d-block w-100 credora-login__submit">
                    Back to sign in
                  </Link>
                </div>
              ) : (
                <Form className="theme-form credora-login__form" onSubmit={handleSubmit} noValidate>
                  <div className="credora-login__account-badge mb-3">
                    <Shield size={16} aria-hidden="true" />
                    <span>
                      <strong>{preview.full_name}</strong>
                      <span className="credora-login__account-badge-email">{preview.email}</span>
                    </span>
                  </div>

                  <FormGroup>
                    <Label className="col-form-label" for="credora-reset-password">
                      New password
                    </Label>
                    <div className="credora-login__input-wrap">
                      <Lock className="credora-login__input-icon" size={18} aria-hidden="true" />
                      <Input
                        id="credora-reset-password"
                        className="form-control credora-login__input credora-login__input--password"
                        type={togglePassword ? "text" : "password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        minLength={8}
                        required
                        autoComplete="new-password"
                        autoFocus
                        placeholder="At least 8 characters"
                      />
                      <button
                        type="button"
                        className="credora-login__toggle-password"
                        onClick={() => setTogglePassword((v) => !v)}
                        aria-label={togglePassword ? "Hide password" : "Show password"}>
                        {togglePassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </FormGroup>

                  <FormGroup>
                    <Label className="col-form-label" for="credora-reset-password-confirm">
                      Confirm password
                    </Label>
                    <div className="credora-login__input-wrap">
                      <Lock className="credora-login__input-icon" size={18} aria-hidden="true" />
                      <Input
                        id="credora-reset-password-confirm"
                        className="form-control credora-login__input credora-login__input--password"
                        type={togglePassword ? "text" : "password"}
                        value={passwordConfirm}
                        onChange={(e) => setPasswordConfirm(e.target.value)}
                        minLength={8}
                        required
                        autoComplete="new-password"
                        placeholder="Retype your password"
                      />
                    </div>
                    {passwordConfirm && !passwordsMatch && (
                      <small className="text-danger d-block mt-1">Passwords do not match.</small>
                    )}
                  </FormGroup>

                  <Btn
                    attrBtn={{
                      color: "primary",
                      className: "credora-login__submit d-block w-100",
                      type: "submit",
                      disabled: !canSubmit,
                    }}>
                    {submitting ? (
                      <>
                        <span className="credora-login__spinner" aria-hidden="true" />
                        Updating password…
                      </>
                    ) : (
                      "Update password"
                    )}
                  </Btn>

                  <p className="text-center mt-3 mb-0" style={{ fontSize: "14px" }}>
                    Remember your password?{" "}
                    <Link to={loginPath}>Sign in</Link>
                  </p>
                </Form>
              )}

              <p className="credora-login__footer-note">
                Protected access for authorized staff only. Activity may be logged for compliance.
              </p>
            </div>
          </main>
        </div>
      </div>
    </Fragment>
  );
};

export default ResetPassword;
